// Single entry point for all AI inference — no AI calls may bypass this module.
//
// This used to hold the Anthropic API key and call api.anthropic.com from the
// browser. Vite inlines every VITE_* variable at build time, so the production
// key was sitting in the public JS bundle at acdyon.io, downloadable by anyone.
// Inference now goes through the backend, which holds the key, authenticates the
// caller, enforces rate limits and a daily spend cap, and logs every call.
//
// There is no client-side API key any more, and there must never be one again:
// scripts/check-bundle-secrets.mjs fails the build if one reappears.

import { supabase } from './supabase'

// Kept for the model-preference UI. The server chooses the model per feature —
// a client cannot select one, which is what stops a stolen session from routing
// everything through the most expensive tier.
export const MODELS = {
  SONNET: 'claude-sonnet-4-6',
  OPUS:   'claude-opus-4-7',
}

const API_BASE = import.meta.env.VITE_API_URL || '/api'

// AI is configured on the server now, so the client cannot tell — and does not
// need to. A misconfigured server returns a clear error from the proxy instead.
export function isAIConfigured() {
  return true
}

async function proxyRequest({ feature, system, messages, maxTokens, stream, signal }) {
  const { data: { session } } = await supabase.auth.getSession()
  if (!session?.access_token) {
    throw new Error('Your session has expired. Sign in again to continue.')
  }

  const resp = await fetch(`${API_BASE}/ai/messages`, {
    method: 'POST',
    headers: {
      'Content-Type':  'application/json',
      Authorization:   `Bearer ${session.access_token}`,
    },
    body: JSON.stringify({ feature, system, messages, maxTokens, stream }),
    signal,
  })

  if (!resp.ok) {
    const err = await resp.json().catch(() => ({}))
    throw new Error(err.message || err.error || `AI request failed (${resp.status})`)
  }

  return resp
}

/**
 * Streaming call. onChunk(text) fires per delta; resolves with the full text.
 * The proxy speaks `data: {"text":"…"}` frames and ends with `data: [DONE]`.
 */
export async function streamAnthropic({ feature, system, messages, maxTokens, onChunk, signal }) {
  const resp = await proxyRequest({ feature, system, messages, maxTokens, stream: true, signal })

  const reader  = resp.body.getReader()
  const decoder = new TextDecoder()
  let buffer = '', fullText = ''

  while (true) {
    const { done, value } = await reader.read()
    if (done) break
    buffer += decoder.decode(value, { stream: true })
    const lines = buffer.split('\n')
    buffer = lines.pop()

    for (const line of lines) {
      if (!line.startsWith('data: ')) continue
      const payload = line.slice(6).trim()
      if (!payload || payload === '[DONE]') continue
      try {
        const evt = JSON.parse(payload)
        // An error frame mid-stream is a real failure, not a chunk to append.
        if (evt.error) throw new Error(evt.error)
        if (evt.text) {
          fullText += evt.text
          onChunk?.(evt.text)
        }
      } catch (e) {
        if (e instanceof SyntaxError) continue
        throw e
      }
    }
  }

  return fullText
}

/** Non-streaming call — returns the full text. */
export async function generateAnthropic({ feature, system, messages, maxTokens, signal }) {
  const resp = await proxyRequest({ feature, system, messages, maxTokens, stream: false, signal })
  const data = await resp.json()
  return data.text || ''
}
